import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const idleTimer = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("praniq_token");
    if (!token) { setUser(false); setReady(true); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("praniq_token"); setUser(false); })
      .finally(() => setReady(true));
  }, []);

  // Re-read the account when the tab regains focus so status changes (e.g. Admin verification) apply without re-login.
  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem("praniq_token")) return null;
    try { const r = await api.get("/auth/me"); setUser(r.data); return r.data; } catch { return null; }
  }, []);
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { refreshUser(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [!!user, refreshUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password, remember = false) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      localStorage.setItem("praniq_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.email_verification_required) {
        return { ok: true, verifyEmail: true, email: data.email, devOtp: data.dev_otp };
      }
      localStorage.setItem("praniq_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const verifyEmail = useCallback(async (email, otp) => {
    try {
      const { data } = await api.post("/auth/verify-email", { email, otp });
      localStorage.setItem("praniq_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback((reason) => {
    localStorage.removeItem("praniq_token");
    if (reason) localStorage.setItem("praniq_session_ended", reason);
    setUser(false);
  }, []);

  // Idle timeout — sign out after inactivity while logged in
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        logout("idle");
        window.location.assign("/login?expired=1");
      }, IDLE_MS);
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  // Surface a "session ended" toast once, after redirect to login
  useEffect(() => {
    const reason = localStorage.getItem("praniq_session_ended");
    if (reason && user === false) {
      toast.info(reason === "idle" ? "Signed out due to inactivity. Please sign in again." : "Your session expired. Please sign in again.");
      localStorage.removeItem("praniq_session_ended");
    }
  }, [user]);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, verifyEmail, logout, updateProfile, setUser, refreshUser }),
    [user, ready, login, register, verifyEmail, logout, updateProfile, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
